import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { SearchEntityType, SearchResult } from './search-api';

/**
 * Where a result opens. The API sends no path (spec 110 §5): the client maps
 * `entity_type` + `entity_id`. Knowledge items have no page of their own
 * yet, so they open their tab of the knowledge base.
 */
export function resultPath(type: SearchEntityType, id: string): string {
  switch (type) {
    case 'content_item':
      return `/content/${id}`;
    case 'campaign':
      return `/campaigns/${id}`;
    case 'media_asset':
      return `/media/${id}`;
    case 'entry':
      return `/knowledge/entries/${id}`;
    case 'knowledge_item':
      return '/knowledge/knowledge-items';
  }
}

/** The translation key of a status token, per type (the API sends the token, never a label). */
function statusKey(result: SearchResult): string | null {
  if (!result.status) return null;
  switch (result.entity_type) {
    case 'content_item':
      return `content.status.${result.status}`;
    case 'campaign':
      return `campaigns.status.${result.status}`;
    case 'knowledge_item':
      return `knowledge.items.status.${result.status}`;
    default:
      return null;
  }
}

/** The snippet text with the server's highlight ranges wrapped in `mark` (offsets, never markup from the API). */
function Highlighted({ text, highlights }: { text: string; highlights: ReadonlyArray<{ offset: number; length: number }> }) {
  const parts: Array<{ text: string; mark: boolean }> = [];
  let position = 0;
  for (const { offset, length } of highlights) {
    if (offset > position) parts.push({ text: text.slice(position, offset), mark: false });
    parts.push({ text: text.slice(offset, offset + length), mark: true });
    position = offset + length;
  }
  if (position < text.length) parts.push({ text: text.slice(position), mark: false });
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>{part.mark ? <mark className="search-result__mark">{part.text}</mark> : part.text}</Fragment>
      ))}
    </>
  );
}

/** One hit (style guide 2.41): linked title, status as text, tenant-written context line, snippet with highlighted terms. */
export function SearchResultRow({ result }: { result: SearchResult }) {
  const { t } = useTranslation();
  const key = statusKey(result);
  return (
    <li className="search-result">
      <div className="ui-row">
        <Link to={resultPath(result.entity_type, result.entity_id)} className="search-result__title">
          {result.title}
        </Link>
        {key ? <span className="ui-status-pill">{t(key)}</span> : null}
        {result.archived && !key ? <span className="ui-status-pill">{t('search.status.archived')}</span> : null}
      </div>
      {result.context_line ? <p className="search-result__context">{result.context_line}</p> : null}
      {result.snippet ? (
        <p className="search-result__snippet">
          <Highlighted text={result.snippet.text} highlights={result.snippet.highlights} />
        </p>
      ) : null}
    </li>
  );
}
